<template>
  <div class="card gender-chart-card">
    <div class="card-header">
      <h3 class="card-title">
        {{ $t('dashboard.demographics.title_gender') }}
        <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltips.genderChartCard')">
          <i class="bi bi-question-circle ms-2 help-icon"></i>
        </span>
      </h3>
    </div>
    <div class="card-body">
      <div class="card-content">
        <div class="donut-chart">
          <canvas ref="chartCanvas"></canvas>
        </div>
        <div class="legend" @click="showDetailModal">
          <div class="legend-item" v-for="item in legendData" :key="item.gender">
            <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
            <span class="legend-label">{{ item.label }}</span>
            <span class="legend-value">{{ item.value.toFixed(1) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <div class="modal fade" id="genderDetailModal" tabindex="-1" aria-labelledby="genderDetailModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="genderDetailModalLabel">{{ $t('dashboard.demographics.title_gender') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="detail-row" v-for="item in legendData" :key="item.gender">
              <span class="detail-gender-label">{{ item.label }}</span>
              <span class="detail-gender-unit">:</span>
              <span class="detail-percentage">{{ item.value.toFixed(1) }}%</span>
              <span class="detail-count">（{{ item.count.toLocaleString() }}{{ $t('dashboard.genderVisitorDetail.peopleUnit') }}）</span>
            </div>
            <div class="detail-row">
              <span class="additional-note">{{ $t('dashboard.genderVisitorDetail.additionalNote') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { Modal } from 'bootstrap';
import axios from 'axios';
import { colors } from '../../../../consts/colors';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const { t: $t } = useI18n();

const props = defineProps({
  dateRange: {
    type: Object,
    required: true
  }
});

const chartCanvas = ref(null);
const legendData = ref([]);
const loading = ref(false);
let chartInstance = null;

// Define all gender groups in order
const genderDefinitions = [
  { key: 'male', labelKey: 'dashboard.demographics.gender_male', color: colors.blue[500] },
  { key: 'female', labelKey: 'dashboard.demographics.gender_female', color: colors.blue[400] },
];

// Initialize legend data with all genders at 0%
const initializeLegendData = () => {
  legendData.value = genderDefinitions.map(def => ({
    gender: def.key,
    label: $t(def.labelKey),
    value: 0,
    color: def.color,
    count: 0
  }));
};

// Fetch statistics data
const fetchStatistics = async () => {
  if (!props.dateRange || !props.dateRange.startDate || !props.dateRange.endDate) {
    return;
  }

  loading.value = true;
  try {
    const startDate = formatDate(props.dateRange.startDate);
    const endDate = formatDate(props.dateRange.endDate);

    const response = await axios.get('/api/user/statistics/dashboard/gender-visitors', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });

    if (response.data && response.data.genders) {
      // Initialize all genders with 0%
      initializeLegendData();

      // Update with actual data from API
      response.data.genders.forEach(item => {
        const legendItem = legendData.value.find(l => l.gender === item.gender);
        if (legendItem) {
          legendItem.value = item.percentage;
          legendItem.count = item.visitor_count;
        }
      });
    } else {
      // If no data, initialize all genders with 0%
      initializeLegendData();
    }

    // Update chart
    updateChart();
  } catch (error) {
    console.error('Error fetching gender statistics:', error);
    // On error, initialize all genders with 0%
    initializeLegendData();
    updateChart();
  } finally {
    loading.value = false;
  }
};

// Format date for API (YYYY-MM-DD)
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initializeChart = () => {
  if (!chartCanvas.value) return;

  const ctx = chartCanvas.value.getContext('2d');
  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: legendData.value.map(item => item.label),
      datasets: [{
        data: legendData.value.map(item => item.value),
        backgroundColor: legendData.value.map(item => item.color),
        borderWidth: 0,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              // Don't show tooltip for empty state
              if (!context.label || context.label === '') {
                return null;
              }
              return context.label + ': ' + context.parsed + '%';
            }
          },
          filter: function(tooltipItem) {
            // Hide tooltip for empty state
            return tooltipItem.label && tooltipItem.label !== '';
          }
        }
      }
    }
  });
};

const updateChart = () => {
  if (!chartInstance) {
    nextTick(() => initializeChart());
    return;
  }

  // Check if all data is 0
  const totalData = legendData.value.reduce((sum, item) => sum + item.value, 0);
  
  if (totalData === 0) {
    // Show empty state with light gray circle
    chartInstance.data.labels = [''];
    chartInstance.data.datasets[0].data = [1];
    chartInstance.data.datasets[0].backgroundColor = [colors.gray[300]];
    chartInstance.data.datasets[0].borderColor = [colors.gray[200]];
    chartInstance.data.datasets[0].borderWidth = 2;
  } else {
    // Update chart data with actual data
    chartInstance.data.labels = legendData.value.map(item => item.label);
    chartInstance.data.datasets[0].data = legendData.value.map(item => item.value);
    chartInstance.data.datasets[0].backgroundColor = legendData.value.map(item => item.color);
    chartInstance.data.datasets[0].borderColor = undefined;
    chartInstance.data.datasets[0].borderWidth = 0;
  }
  
  chartInstance.update();
};

onMounted(() => {
  // Initialize legend data first
  initializeLegendData();
  // Then fetch actual statistics
  fetchStatistics();
});

// Watch for date range changes
watch(() => props.dateRange, () => {
  fetchStatistics();
}, { deep: true });

// Modal instance
let modalInstance = null;

// Show detail modal
const showDetailModal = () => {
  if (!modalInstance) {
    const modalElement = document.getElementById('genderDetailModal');
    if (modalElement) {
      modalInstance = new Modal(modalElement);
    }
  }
  modalInstance?.show();
};
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
}

.card-header {
  background-color: v-bind('colors.white');
  border: none;
  padding: 0;
  margin-bottom: 16px;
}

.card-body {
  padding: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin: 0;

  .help-icon {
    font-size: 18px;
    color: v-bind('colors.textLight');
    cursor: pointer;
  }
}

.gender-chart-card {

  .card-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }

  .donut-chart {
    height: 200px;

    @media (max-width: 768px) {
      height: 250px;
      max-width: 250px;
      margin: 0 auto;
    }
  }

  .legend {
    cursor: pointer;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
      font-size: 13px;

      .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 2px;
      }

      .legend-label {
        flex: 1;
        color: v-bind('colors.textPrimary');
      }

      .legend-value {
        color: v-bind('colors.gray[500]');
      }
    }
  }
}

// Modal styles
.modal-content {
  .modal-header {
    background-color: v-bind('colors.white');
    border-bottom: 1px solid v-bind('colors.gray[200]');

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .modal-body {
    padding: 24px;

    .detail-row {
      display: grid;
      grid-template-columns: auto auto auto 1fr;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid v-bind('colors.gray[100]');
      gap: 8px;

      &:last-child {
        border-bottom: none;
      }

      .detail-gender-label {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: left;
        min-width: 60px;
      }

      .detail-gender-unit {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
      }

      .detail-percentage {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: right;
        min-width: 50px;
      }

      .detail-count {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
      }

      .additional-note {
        font-size: 12px;
        font-weight: 400;
        white-space: normal;
        color: v-bind('colors.textLight');
        word-wrap: break-word;
        overflow-wrap: break-word;
        grid-column: 1 / -1;
      }
    }
  }
}
</style>
